import React, { FormEvent, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import api from "../../api";
import { useApi } from "../../hooks/useApi";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage, getFieldErrors } from "../../utils/apiError";
import { Option } from "../../constants/enums";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button, { IconButton } from "../../components/ui/Button";
import DataTable, { Column, RowActions } from "../../components/ui/DataTable";
import { SearchInput, Toolbar } from "../../components/ui/Toolbar";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { Input, Segmented, Select } from "../../components/ui/FormField";

export interface MasterField {
    name: string;
    label: string;
    kind: "text" | "number" | "select" | "segmented";
    options?: Option[];
    required?: boolean;
    placeholder?: string;
    hint?: string;
    half?: boolean;
    min?: number;
    max?: number;
}

type FormState = Record<string, string>;

interface MasterPageProps<T extends { id: number; name: string }> {
    title: string;
    subtitle: string;
    /** e.g. "/device-type" */
    endpoint: string;
    /** e.g. "device type" — used in messages */
    singular: string;
    fields: MasterField[];
    columns: Column<T>[];
    emptyForm: FormState;
    toForm: (row: T) => FormState;
    /** Convert string form values to the API body (numbers etc.) */
    toPayload?: (form: FormState) => Record<string, unknown>;
    /** Shown in the delete dialog, e.g. "Used by 3 products" */
    usage?: (row: T) => number;
}

export default function MasterPage<T extends { id: number; name: string }>({
    title,
    subtitle,
    endpoint,
    singular,
    fields,
    columns,
    emptyForm,
    toForm,
    toPayload = (f) => f,
    usage,
}: MasterPageProps<T>) {
    const toast = useToast();
    const { data, loading, error, reload } = useApi<T[]>(endpoint);

    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState<T | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState<T | null>(null);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!data) return null;
        return q ? data.filter((r) => r.name.toLowerCase().includes(q)) : data;
    }, [data, search]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setFieldErrors({});
        setFormOpen(true);
    };

    const openEdit = (row: T) => {
        setEditing(row);
        setForm(toForm(row));
        setFieldErrors({});
        setFormOpen(true);
    };

    const set = (name: string, value: string) => {
        setForm((f) => ({ ...f, [name]: value }));
        setFieldErrors((e) => ({ ...e, [name]: "" }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        // Segmented has no native "required", check here
        const missing = fields.find((f) => f.required && !form[f.name]?.toString().trim());
        if (missing) {
            setFieldErrors({ [missing.name]: `${missing.label} is required` });
            return;
        }

        setSaving(true);
        try {
            const body = toPayload(form);
            if (editing) {
                await api.put(`${endpoint}/${editing.id}`, body);
                toast.success(`${capitalize(singular)} updated`);
            } else {
                await api.post(endpoint, body);
                toast.success(`${capitalize(singular)} created`);
            }
            setFormOpen(false);
            reload();
        } catch (err) {
            setFieldErrors(getFieldErrors(err));
            toast.error(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleting) return;
        try {
            await api.delete(`${endpoint}/${deleting.id}`);
            toast.success(`${capitalize(singular)} deleted`);
            setDeleting(null);
            reload();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const allColumns: Column<T>[] = [
        ...columns,
        {
            key: "actions",
            header: "",
            className: "w-24 text-right",
            render: (row) => (
                <RowActions>
                    <IconButton icon={Pencil} label="Edit" onClick={() => openEdit(row)} />
                    <IconButton icon={Trash2} label="Delete" tone="danger" onClick={() => setDeleting(row)} />
                </RowActions>
            ),
        },
    ];

    const deleteUsage = deleting && usage ? usage(deleting) : 0;

    return (
        <>
            <PageHeader
                title={title}
                subtitle={subtitle}
                actions={
                    <Button icon={Plus} onClick={openCreate}>
                        Add {singular}
                    </Button>
                }
            />

            <Card padded={false}>
                <Toolbar>
                    <SearchInput value={search} onChange={setSearch} placeholder={`Search ${title.toLowerCase()}…`} />
                    {data && <span className="text-xs text-slate-500 sm:ml-auto">{rows?.length ?? 0} of {data.length}</span>}
                </Toolbar>
                <DataTable
                    columns={allColumns}
                    rows={rows}
                    loading={loading}
                    error={error}
                    onRetry={reload}
                    rowKey={(r) => r.id}
                    emptyTitle={search ? "No matches" : `No ${title.toLowerCase()} yet`}
                    emptyText={search ? "Try a different search." : `Add your first ${singular} to get started.`}
                    emptyAction={
                        !search && (
                            <Button icon={Plus} size="sm" onClick={openCreate}>
                                Add {singular}
                            </Button>
                        )
                    }
                />
            </Card>

            <Modal
                open={formOpen}
                onClose={() => setFormOpen(false)}
                locked={saving}
                title={editing ? `Edit ${singular}` : `Add ${singular}`}
                size="sm"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button type="submit" form="master-form" loading={saving}>
                            {editing ? "Save changes" : "Create"}
                        </Button>
                    </>
                }
            >
                <form id="master-form" onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                    {fields.map((f) => {
                        const common = {
                            label: f.label,
                            required: f.required,
                            error: fieldErrors[f.name],
                        };
                        const span = f.half ? "col-span-2 sm:col-span-1" : "col-span-2";

                        if (f.kind === "segmented") {
                            return (
                                <div key={f.name} className={span}>
                                    <Segmented {...common} value={form[f.name] ?? ""} options={f.options ?? []} onChange={(v) => set(f.name, v)} />
                                </div>
                            );
                        }

                        if (f.kind === "select") {
                            return (
                                <Select
                                    key={f.name}
                                    {...common}
                                    wrapperClassName={span}
                                    value={form[f.name] ?? ""}
                                    onChange={(e) => set(f.name, e.target.value)}
                                    options={f.options ?? []}
                                    placeholder={f.placeholder ?? "Select…"}
                                    hint={f.hint}
                                />
                            );
                        }

                        return (
                            <Input
                                key={f.name}
                                {...common}
                                wrapperClassName={span}
                                type={f.kind}
                                min={f.min}
                                max={f.max}
                                value={form[f.name] ?? ""}
                                onChange={(e) => set(f.name, e.target.value)}
                                placeholder={f.placeholder}
                                hint={f.hint}
                                autoFocus={f === fields[0]}
                            />
                        );
                    })}
                </form>
            </Modal>

            <ConfirmDialog
                open={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={handleDelete}
                title={`Delete ${singular}?`}
                message={
                    deleteUsage > 0 ? (
                        <>
                            <b className="text-white">{deleting?.name}</b> is still used by <b className="text-white">{deleteUsage}</b> record(s), so
                            it can't be deleted until those are changed or removed.
                        </>
                    ) : (
                        <>
                            <b className="text-white">{deleting?.name}</b> will be removed. This can't be undone.
                        </>
                    )
                }
            />
        </>
    );
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Small "used by N" pill for masters tables */
export function UsageCount({ count }: { count: number | undefined }) {
    const n = count ?? 0;
    return <span className={`text-sm font-semibold ${n > 0 ? "text-slate-200" : "text-slate-600"}`}>{n}</span>;
}

/** Shared name/created columns */
export const nameColumn = <T extends { name: string }>(): Column<T> => ({
    key: "name",
    header: "Name",
    render: (r) => <span className="font-semibold text-white">{r.name}</span>,
});

export const Muted = ({ children }: { children: React.ReactNode }) => <span className="text-slate-500">{children}</span>;